import React, { useEffect, useMemo, useState } from "react";
import { Select } from "@mantine/core";
import "../Admin/Event/AdminEvent.css";

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkOverviewDto {
    name: string;
    date: string;
    beer: number;
    water: number;
    wine: number;
    total: number;
}

const AdminDrinkOverview: React.FC = () => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [overview, setOverview] = useState<DrinkOverviewDto[]>([]);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedPerson, setSelectedPerson] = useState<string | null>(null);

    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [availablePersons, setAvailablePersons] = useState<string[]>([]);

    // ---------------- HENT EVENTS ----------------
    useEffect(() => {
        fetch("/api/events")
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) setEvents(data);
            });
    }, []);

    // ---------------- HENT OVERVIEW ----------------
    const fetchOverview = async (event: number) => {
        if (!event) return;

        const res = await fetch(`/api/drinks/overview?eventId=${event}`);
        if (!res.ok) return;

        const data: DrinkOverviewDto[] = await res.json();
        setOverview(data);

        // Unikke datoer
        const dates = Array.from(
            new Set(data.map(d => d.date.substring(0, 10)))
        ).sort();

        // Unikke personer
        const persons = Array.from(
            new Set(data.map(d => d.name))
        ).sort();

        setAvailableDates(dates);
        setAvailablePersons(persons);
    };

    // Når event ændres
    useEffect(() => {
        if (eventId) {
            setSelectedDate(null);
            setSelectedPerson(null);
            fetchOverview(eventId);
        } else {
            setOverview([]);
            setAvailableDates([]);
            setAvailablePersons([]);
        }
    }, [eventId]);

    // ---------------- FILTRERING ----------------
    const filteredOverview = useMemo(() => {
        return overview.filter(row => {
            const matchesDate =
                !selectedDate ||
                row.date.substring(0, 10) === selectedDate;

            const matchesPerson =
                !selectedPerson ||
                row.name === selectedPerson;

            return matchesDate && matchesPerson;
        });
    }, [overview, selectedDate, selectedPerson]);

    // ---------------- TOTALER ----------------
    const totals = useMemo(() => {
        return filteredOverview.reduce(
            (acc, row) => {
                acc.beer += row.beer;
                acc.water += row.water;
                acc.wine += row.wine;
                acc.total += row.total;
                return acc;
            },
            { beer: 0, water: 0, wine: 0, total: 0 }
        );
    }, [filteredOverview]);

    return (
        <div className="form-container">
            <h2>Drinks Oversigt</h2>

            {/* EVENT SELECT */}
            <label>Event *</label>
            <Select
                placeholder="Vælg event"
                value={eventId ? String(eventId) : null}
                onChange={(val) => setEventId(val ? Number(val) : 0)}
                data={events.map(e => ({
                    value: String(e.eventID),
                    label: e.eventName || `Event ${e.eventID}`
                }))}
            />

            {/* DATO SELECT */}
            {availableDates.length > 0 && (
                <>
                    <label style={{ marginTop: "15px" }}>Dato</label>
                    <Select
                        placeholder="Alle datoer"
                        clearable
                        value={selectedDate}
                        onChange={setSelectedDate}
                        data={availableDates.map(d => ({
                            value: d,
                            label: d
                        }))}
                    />
                </>
            )}

            {/* PERSON SELECT */}
            {availablePersons.length > 0 && (
                <>
                    <label style={{ marginTop: "15px" }}>Person</label>
                    <Select
                        placeholder="Alle personer"
                        searchable
                        clearable
                        value={selectedPerson}
                        onChange={setSelectedPerson}
                        data={availablePersons.map(p => ({
                            value: p,
                            label: p
                        }))}
                    />
                </>
            )}

            {/* TABEL */}
            {filteredOverview.length > 0 && (
                <table
                    style={{
                        marginTop: "20px",
                        width: "100%",
                        borderCollapse: "collapse",
                        textAlign: "center"
                    }}
                >
                    <thead>
                        <tr style={{ background: "#f4f4f4" }}>
                            <th style={thStyleLeft}>Navn</th>
                            <th style={thStyle}>Dato</th>
                            <th style={thStyle}>🍺 Øl</th>
                            <th style={thStyle}>🥤 Vand</th>
                            <th style={thStyle}>🍷 Vin</th>
                            <th style={thStyle}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOverview.map((row, index) => (
                            <tr key={index}>
                                <td style={tdStyleLeft}>{row.name}</td>
                                <td style={tdStyle}>
                                    {row.date.substring(0, 10)}
                                </td>
                                <td style={tdStyle}>{row.beer}</td>
                                <td style={tdStyle}>{row.water}</td>
                                <td style={tdStyle}>{row.wine}</td>
                                <td style={{ ...tdStyle, fontWeight: "bold" }}>
                                    {row.total}
                                </td>
                            </tr>
                        ))}
                    </tbody>

                    <tfoot>
                        <tr style={{ background: "#eaeaea", fontWeight: "bold" }}>
                            <td style={tdStyleLeft}>TOTAL</td>
                            <td style={tdStyle}></td>
                            <td style={tdStyle}>{totals.beer}</td>
                            <td style={tdStyle}>{totals.water}</td>
                            <td style={tdStyle}>{totals.wine}</td>
                            <td style={tdStyle}>{totals.total}</td>
                        </tr>
                    </tfoot>
                </table>
            )}

            {filteredOverview.length === 0 && eventId > 0 && (
                <p style={{ marginTop: "15px" }}>Ingen drinks fundet</p>
            )}
        </div>
    );
};

// ---------------- STYLING ----------------
const thStyle = {
    padding: "10px",
    borderBottom: "2px solid #ddd"
};

const thStyleLeft = {
    ...thStyle,
    textAlign: "left" as const
};

const tdStyle = {
    padding: "8px",
    borderBottom: "1px solid #eee"
};

const tdStyleLeft = {
    ...tdStyle,
    textAlign: "left" as const
};

export default AdminDrinkOverview;
